import { create } from 'zustand';
import {
  createEmptyCreateNodeResponseDraft,
  createEmptyPromptPreview,
  createEmptyResponseDraft,
  type PromptPreview,
} from '../../../core/entities/ai-assist-config';
import type { DeIdentificationRule } from '../../../core/entities/ai-assist-config';
import { applyDeidentificationRules } from '../../../core/policies/anonymization/apply-deidentification-rules';
import { assembleDeidentificationRules } from '../../../core/policies/anonymization/assemble-deidentification-rules';
import { renderCreateNodePromptPreview } from '../../../core/policies/anonymization/render-create-node-prompt-preview';
import { renderPromptPreview } from '../../../core/policies/anonymization/render-prompt-preview';
import { validateAIResponse } from '../../../core/policies/anonymization/validate-ai-response';
import { validateCreateNodeResponse } from '../../../core/policies/anonymization/validate-create-node-response';
import { canCreateNodeInAccessMode } from '../../../core/policies/access-gate/preview-node-entitlement';
import type { GraphDocument } from '../../../core/entities/graph-document';
import type { JourneyNode } from '../../../core/entities/node';
import { applyAIOperations, normalizeCreateNodeDraftToOperation } from '../../../core/services/apply-ai-operations-service';
import { useGraphStore } from '../../graph-view/services/graph-store';
import { getCurrentGraphAIAssistConfig, useDeidentificationSettingsStore } from '../../preferences/services/deidentification-settings-store';

interface AIAssistWorkspaceState {
  selectedNodeIds: string[];
  userPromptText: string;
  sourceJson: string;
  maskedJson: string;
  promptPreview: PromptPreview;
  responseDraft: ReturnType<typeof createEmptyResponseDraft>;
  createNodeUserPromptText: string;
  createNodePromptPreview: string;
  createNodeResponseDraft: ReturnType<typeof createEmptyCreateNodeResponseDraft>;
  operationsApplyIssues: string[];
  createNodeApplyIssues: string[];
  stale: boolean;
  toggleNodeSelection(nodeId: string): void;
  setSelectedNodeIds(nodeIds: string[]): void;
  setUserPromptText(userPromptText: string): void;
  buildSourceBundle(graph: GraphDocument): void;
  buildMaskedBundle(): void;
  buildPromptPreview(): void;
  setResponseDraft(rawText: string): void;
  setCreateNodeUserPromptText(userPromptText: string): void;
  setCreateNodeResponseDraft(rawText: string): void;
  applyResponseDraft(): void;
  applyCreateNodeResponseDraft(): void;
  markStale(): void;
  reset(): void;
}

export const useAIAssistWorkspaceStore = create<AIAssistWorkspaceState>((set, get) => ({
  selectedNodeIds: [],
  userPromptText: '',
  sourceJson: '',
  maskedJson: '',
  promptPreview: createEmptyPromptPreview(),
  responseDraft: createEmptyResponseDraft(),
  createNodeUserPromptText: '',
  createNodePromptPreview: buildCreateNodePromptPreview(''),
  createNodeResponseDraft: createEmptyCreateNodeResponseDraft(),
  operationsApplyIssues: [],
  createNodeApplyIssues: [],
  stale: false,
  toggleNodeSelection(nodeId) {
    set((state) => {
      const selectedNodeIds = state.selectedNodeIds.includes(nodeId)
        ? state.selectedNodeIds.filter((selectedNodeId) => selectedNodeId !== nodeId)
        : [...state.selectedNodeIds, nodeId];

      return {
        selectedNodeIds,
        ...buildWorkspaceBundles(selectedNodeIds, state.userPromptText),
      };
    });
  },
  setSelectedNodeIds(nodeIds) {
    const selectedNodeIds = [...new Set(nodeIds)];
    set((state) => ({
      selectedNodeIds,
      ...buildWorkspaceBundles(selectedNodeIds, state.userPromptText),
    }));
  },
  setUserPromptText(userPromptText) {
    set((state) => ({
      userPromptText,
      ...buildWorkspaceBundles(state.selectedNodeIds, userPromptText),
    }));
  },
  buildSourceBundle(graph) {
    void graph;
    const state = get();
    set(buildWorkspaceBundles(state.selectedNodeIds, state.userPromptText));
  },
  buildMaskedBundle() {
    const state = get();
    set(buildWorkspaceBundles(state.selectedNodeIds, state.userPromptText));
  },
  buildPromptPreview() {
    const state = get();
    set(buildWorkspaceBundles(state.selectedNodeIds, state.userPromptText));
  },
  setResponseDraft(rawText) {
    set({
      responseDraft: validateAIResponse(rawText),
      operationsApplyIssues: [],
    });
  },
  setCreateNodeUserPromptText(userPromptText) {
    set({
      createNodeUserPromptText: userPromptText,
      createNodePromptPreview: buildCreateNodePromptPreview(userPromptText),
      createNodeApplyIssues: [],
    });
  },
  setCreateNodeResponseDraft(rawText) {
    set({
      createNodeResponseDraft: validateCreateNodeResponse(rawText),
      createNodeApplyIssues: [],
    });
  },
  applyResponseDraft() {
    const state = get();
    if (state.responseDraft.validationStatus !== 'valid' || !state.responseDraft.parsedEnvelope) {
      set({ operationsApplyIssues: ['AI response must be valid before import.'] });
      return;
    }

    const result = runApply(state.responseDraft.parsedEnvelope.operations);
    if (!result.applied || !result.graph) {
      set({ operationsApplyIssues: result.issues.map((issue) => issue.message) });
      return;
    }

    useGraphStore.getState().replaceGraph(result.graph);
    set(resetWorkspaceState());
  },
  applyCreateNodeResponseDraft() {
    const state = get();
    if (state.createNodeResponseDraft.validationStatus !== 'valid' || !state.createNodeResponseDraft.parsedPayload) {
      set({ createNodeApplyIssues: ['Create-node JSON must be valid before import.'] });
      return;
    }

    const result = runApply([normalizeCreateNodeDraftToOperation(state.createNodeResponseDraft.parsedPayload)]);
    if (!result.applied || !result.graph) {
      set({ createNodeApplyIssues: result.issues.map((issue) => issue.message) });
      return;
    }

    useGraphStore.getState().replaceGraph(result.graph);
    set(resetWorkspaceState());
  },
  markStale() {
    set((state) => ({
      stale: !!(
        state.sourceJson
        || state.maskedJson
        || state.promptPreview.renderedPrompt
        || state.responseDraft.rawText
        || state.createNodeUserPromptText.trim()
        || state.createNodeResponseDraft.rawText
      ),
      promptPreview: state.promptPreview.renderedPrompt ? { ...state.promptPreview, status: 'stale' } : state.promptPreview,
    }));
  },
  reset() {
    set(resetWorkspaceState());
  },
}));

function buildWorkspaceBundles(selectedNodeIds: string[], userPromptText: string): Pick<AIAssistWorkspaceState, 'sourceJson' | 'maskedJson' | 'promptPreview' | 'stale'> {
  const graph = useGraphStore.getState().graph;
  const config = getCurrentGraphAIAssistConfig();
  const selectedNodes = graph.nodes.filter((node) => selectedNodeIds.includes(node.nodeId));

  if (selectedNodes.length === 0) {
    return {
      sourceJson: '',
      maskedJson: '',
      promptPreview: createEmptyPromptPreview(),
      stale: false,
    };
  }

  const sourceJson = JSON.stringify(selectedNodes, null, 2);
  const assembly = assembleDeidentificationRules(config.deIdentification);
  const maskResults = config.deIdentification.enabled
    ? selectedNodes.map((node) => maskNodeBundle(node, assembly.activeRules))
    : selectedNodes.map((node) => ({ value: node, appliedRuleIds: [] }));
  const maskedNodes = maskResults.map((result) => result.value);
  const appliedRuleIds = [...new Set(maskResults.flatMap((result) => result.appliedRuleIds))];
  const maskedJson = JSON.stringify(maskedNodes, null, 2);

  return {
    sourceJson,
    maskedJson,
    promptPreview: renderPromptPreview({
      promptContract: config.promptContract,
      maskedBundle: maskedJson,
      activeRules: assembly.activeRules,
      appliedRuleIds,
      userPromptText,
    }),
    stale: false,
  };
}

function maskNodeBundle(node: JourneyNode, rules: DeIdentificationRule[]): { value: JourneyNode; appliedRuleIds: string[] } {
  return maskBundleValue(node, rules);
}

function runApply(operations: Array<{ action: 'create' | 'update'; nodeType: 'Human' | 'Project' | 'Occupation'; nodeId?: string; fields: Record<string, unknown> }>) {
  const config = getCurrentGraphAIAssistConfig();
  const assembly = assembleDeidentificationRules(config.deIdentification);
  const graphState = useGraphStore.getState();
  const createCount = operations.filter((operation) => operation.action === 'create').length;

  if (createCount > 0 && !canCreateNodeInAccessMode(graphState.accessMode, graphState.graph.nodes.length + createCount - 1)) {
    return {
      applied: false,
      graph: null,
      issues: [{ operationIndex: -1, message: 'Preview mode can create up to 10 nodes. For the full experience, contact the original author.' }],
      createdNodeIds: [],
      updatedNodeIds: [],
    };
  }

  return applyAIOperations(graphState.graph, operations, {
    owner: !graphState.readonlyMode,
    rules: assembly.activeRules,
    connectionStrategy: graphState.connectionStrategy,
  });
}

function buildCreateNodePromptPreview(userPromptText: string): string {
  const config = getCurrentGraphAIAssistConfig();
  return renderCreateNodePromptPreview({
    promptContract: config.createNodePromptContract,
    userPromptText,
  });
}

function resetWorkspaceState(): Pick<
  AIAssistWorkspaceState,
  | 'selectedNodeIds'
  | 'userPromptText'
  | 'sourceJson'
  | 'maskedJson'
  | 'promptPreview'
  | 'responseDraft'
  | 'createNodeUserPromptText'
  | 'createNodePromptPreview'
  | 'createNodeResponseDraft'
  | 'operationsApplyIssues'
  | 'createNodeApplyIssues'
  | 'stale'
> {
  return {
    selectedNodeIds: [],
    userPromptText: '',
    sourceJson: '',
    maskedJson: '',
    promptPreview: createEmptyPromptPreview(),
    responseDraft: createEmptyResponseDraft(),
    createNodeUserPromptText: '',
    createNodePromptPreview: buildCreateNodePromptPreview(''),
    createNodeResponseDraft: createEmptyCreateNodeResponseDraft(),
    operationsApplyIssues: [],
    createNodeApplyIssues: [],
    stale: false,
  };
}

function maskBundleValue<T>(value: T, rules: DeIdentificationRule[]): { value: T; appliedRuleIds: string[] } {
  if (typeof value === 'string') {
    const result = applyDeidentificationRules(value, rules);
    return { value: result.text as T, appliedRuleIds: result.appliedRuleIds };
  }

  if (Array.isArray(value)) {
    const maskedItems = value.map((item) => maskBundleValue(item, rules));
    return {
      value: maskedItems.map((item) => item.value) as T,
      appliedRuleIds: maskedItems.flatMap((item) => item.appliedRuleIds),
    };
  }

  if (value && typeof value === 'object') {
    const entries = Object.entries(value).map(([key, entryValue]) => [key, maskBundleValue(entryValue, rules)] as const);
    return {
      value: Object.fromEntries(entries.map(([key, entryValue]) => [key, entryValue.value])) as T,
      appliedRuleIds: entries.flatMap(([, entryValue]) => entryValue.appliedRuleIds),
    };
  }

  return { value, appliedRuleIds: [] };
}

let aiWorkspaceSyncStarted = false;

export function ensureAIWorkspaceSync() {
  if (aiWorkspaceSyncStarted) {
    return;
  }
  aiWorkspaceSyncStarted = true;
  useGraphStore.subscribe((state, previousState) => {
    if (state.graph !== previousState.graph) {
      useAIAssistWorkspaceStore.getState().markStale();
    }
  });
  useDeidentificationSettingsStore.subscribe((state, previousState) => {
    if (state.config !== previousState.config) {
      useAIAssistWorkspaceStore.getState().markStale();
    }
  });
}

export function resetAIAssistWorkspace() {
  useAIAssistWorkspaceStore.getState().reset();
}
